"use client";

import { useEffect, useState } from "react";

const Pagination = ({ totalItems, onPageChanged, perPage }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalItems / perPage);
  const pageNeighbours = 2;

  const LEFT_PAGE = "LEFT";
  const RIGHT_PAGE = "RIGHT";

  /**
   * Helper method for creating a range of numbers
   * range(1, 5) => [1, 2, 3, 4, 5]
   */
  const range = (from, to, step = 1) => {
    let i = from;
    const range = [];

    while (i <= to) {
      range.push(i);
      i += step;
    }

    return range;
  };

  const activeClass =
    "w-10 h-10 text-white bg-primary-1000   transition-colors duration-150 rounded-full focus:shadow-outline hover:bg-primary-1000   hover:text-white";
  const inActiveClass =
    "w-10 h-10 text-primary-1000 bg-primary-50/50  transition-colors duration-150 rounded-full focus:shadow-outline hover:bg-primary-1000   hover:text-white";
 
  const fetchPageNumbers = () => {
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);
      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, RIGHT_PAGE];
          break;
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  const gotoPage = (page) => {
    const currentPage = Math.max(0, Math.min(page, totalPages));
    setCurrentPage(page);
    onPageChanged && onPageChanged(currentPage);
  };

  const handleClick = (page) => {
    gotoPage(page);
  };

  const handleMoveLeft = (evt) => {
    evt.preventDefault();
    gotoPage(currentPage - pageNeighbours * 2 - 1);
  };

  const handleMoveRight = (evt) => {
    evt.preventDefault();
    gotoPage(currentPage + pageNeighbours * 2 + 1);
  };

  useEffect(() => {
    gotoPage(1);
  }, []);

  return (
    <>
      <ul className="flex space-x-2 justify-center mt-10">
        {fetchPageNumbers().map((page, index) => {
          if (page === LEFT_PAGE)
            return (
              <li key={index}>
                <button
                  className="flex items-center justify-center w-10 h-10 text-primary-600 bg-primary-50/50  transition-colors duration-150 rounded-full focus:shadow-outline hover:bg-primary-100"
                  onClick={handleMoveLeft}
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                      fillRule="evenodd"
                      fill="#ED2B50"
                    />
                  </svg>
                </button>
              </li>
            );

          if (page === RIGHT_PAGE)
            return (
              <li key={index}>
                <button
                  className="flex items-center justify-center w-10 h-10 text-primary-600 bg-primary-50/50  transition-colors duration-150 rounded-full focus:shadow-outline hover:bg-primary-100"
                  onClick={handleMoveRight}
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                      fillRule="evenodd"
                      fill="#ED2B50"
                    />
                  </svg>
                </button>
              </li>
            );

          return (
            <li key={index}>
              <button
                className={currentPage === page ? activeClass : inActiveClass}
                onClick={() => handleClick(page)}
              >
                {page}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default Pagination;
